import {
  Bell,
  CheckSquare,
  Link2,
  MoreHorizontal,
  Phone,
  Sparkles,
  UserPlus,
} from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import type {
  NotificationCategory,
  NotificationSchema,
} from '@/services/api/notificationsService';
import { stripMarkdownSyntax } from '@/utils/markdown';

interface NotificationListItemProps extends React.HTMLAttributes<HTMLDivElement> {
  notification: NotificationSchema;
  isSelected: boolean;
  onSelect: () => void;
  onMarkRead: (markedUnread: boolean) => void;
  /** Whether to show the icon. Defaults to true. Set to false for mentions tab. */
  showIcon?: boolean;
}

/**
 * Extract the person/source name from notification title.
 * Parses patterns like "Carol Weaver mentioned you..." to get "Carol Weaver".
 */
const getSourceName = (title: string): string | null => {
  const mentionMatch = title.match(/^([A-Z][a-z]+\s+[A-Z][a-z]+)/);
  if (mentionMatch) {
    return mentionMatch[1];
  }
  return null;
};

/**
 * Get the action/title part of the notification (without the person's name).
 */
const getActionTitle = (title: string): string => {
  const sourceName = getSourceName(title);
  if (sourceName) {
    // Remove the name and clean up common patterns
    const action = title.replace(sourceName, '').trim();
    // Clean up "mentioned you in a task" -> "mentioned you in a task"
    return action;
  }
  return title;
};

/**
 * Get icon based on notification category.
 */
const getIcon = (category: NotificationCategory | undefined, title: string) => {
  const iconClass = 'w-3 h-3';

  // Check title patterns for more specific icons
  if (title.toLowerCase().includes('call')) {
    return <Phone className={iconClass} />;
  }
  if (title.toLowerCase().includes('reroute') || title.toLowerCase().includes('route')) {
    return <Link2 className={iconClass} />;
  }
  if (title.toLowerCase().includes('task') || title.toLowerCase().includes('assigned')) {
    return <CheckSquare className={iconClass} />;
  }

  // Fall back to category
  switch (category) {
    case 'ASSIGNMENT':
      return <UserPlus className={iconClass} />;
    case 'SUGGESTION':
      return <Sparkles className={iconClass} />;
    default:
      return <Bell className={iconClass} />;
  }
};

/**
 * Format a timestamp to relative time (e.g., "5m", "2h", "3d")
 */
const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m`;
  if (hours < 24) return `${hours}h`;
  if (days < 7) return `${days}d`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export const NotificationListItem: React.FC<NotificationListItemProps> = ({
  notification,
  isSelected,
  onSelect,
  onMarkRead,
  showIcon = true,
  className,
  ...rest
}) => {
  const isUnread = !notification.read_at || notification.marked_unread;
  const actionTitle = getActionTitle(notification.title);
  const icon = getIcon(notification.category, notification.title);

  return (
    <div
      className={cn(
        'group relative flex items-start gap-2.5 px-3 py-2.5 cursor-pointer transition-colors border-l-2',
        isSelected
          ? 'bg-indigo-50 border-l-indigo-600'
          : isUnread
            ? 'bg-indigo-50 border-l-transparent hover:bg-gray-50'
            : 'bg-white border-l-transparent hover:bg-gray-50',
        className,
      )}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onSelect();
        }
      }}
      role="button"
      tabIndex={0}
      {...rest}
    >
      {/* Icon - only show if showIcon is true */}
      {showIcon && (
        <div
          className={cn(
            'flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center',
            isUnread
              ? 'bg-indigo-100 text-indigo-600'
              : 'bg-gray-100 text-gray-500',
          )}
        >
          {icon}
        </div>
      )}

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Top row: Person name + time + unread dot */}
        <div className="flex items-center justify-between gap-2">
          <span
            className={cn(
              'text-xs truncate font-bold',
              showIcon ? 'text-gray-600' : 'text-gray-900',
            )}
          >
            {notification.matter_name || 'Unknown matter'}
          </span>
          <div className="flex items-center gap-1 flex-shrink-0">
            <span className="text-[8px] text-gray-500 font-semibold">
              {formatRelativeTime(notification.created_at)}
            </span>
            {isUnread && (
              <span className="w-1.5 h-1.5 rounded-full bg-indigo-600 flex-shrink-0" />
            )}
          </div>
        </div>

        {/* For mentions tab (!showIcon): show "Person: content", for updates: show title + description */}
        {!showIcon ? (
          <p
            className={cn(
              'text-[11px] leading-snug line-clamp-2',
              isUnread ? 'font-medium text-gray-700' : 'font-medium text-gray-600',
            )}
          >
            {notification.title && <span className="font-semibold">{notification.title.split(' ')[0]}: </span>}
            {stripMarkdownSyntax(notification.description || actionTitle)}
            
          </p>
        ) : (
          <>
            <p
              className={cn(
                'text-[12px] leading-snug line-clamp-1',
                isUnread ? 'font-medium text-gray-700' : 'font-medium text-gray-600',
                showIcon ? 'text-gray-900 font-bold' : 'text-gray-900',
              )}
            >
              {notification.title}
            </p>
            {notification.description && (
              <p className="text-[11px] text-gray-500 font-medium line-clamp-2 leading-snug">
                {stripMarkdownSyntax(notification.description)}
              </p>
            )}
          </>
        )}
      </div>

      {/* Hover menu */}
      <div className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              onClick={(e) => e.stopPropagation()}
              className="p-1 rounded-md border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 shadow-sm"
            >
              <MoreHorizontal className="w-3.5 h-3.5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[130px] rounded-lg shadow-lg border-0 ring-1 ring-black/5 p-1">
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                onMarkRead(!isUnread);
              }}
              className="text-xs font-medium text-gray-700 rounded-md px-2.5 py-1.5 cursor-pointer focus:bg-gray-100"
            >
              {isUnread ? 'Mark as read' : 'Mark as unread'}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};
